import { columnIsHeader, rowIsHeader } from '@tiptap/pm/tables';
import { isNodeSelection, type Editor } from '@tiptap/react';
import {
    BetweenHorizonalStart,
    BetweenVerticalStart,
    Columns3Cog,
    Database,
    Grid2X2X,
    KeyRound,
    Table,
    TableCellsMerge,
    TableCellsSplit,
    Trash2,
} from 'lucide-react';
import * as React from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Icons ---
import { ChevronDownIcon } from '@/components/tiptap-editor/tiptap-icons/chevron-down-icon';

// --- Lib ---
import { getTable } from '@/components/tiptap-editor/tiptap-node/table-node/lib/tiptap-table-utils';
import { isNodeInSchema } from '@/lib/tiptap-utils';

// --- Table insert ---
import { TableInsertGridPanel } from '@/components/tiptap-editor/tiptap-node/table-node/ui/table-trigger-button/table-insert-grid-panel';

// --- UI Primitives ---
import type { ButtonProps } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/tiptap-editor/tiptap-ui-primitive/dropdown-menu';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/tiptap-editor/tiptap-ui-primitive/popover';
import { Separator } from '@/components/tiptap-editor/tiptap-ui-primitive/separator';
import { getParentNodeOfType, isSelectionDescendantOf } from '@/lib/zy-tiptap-utils';
import { Fragment } from 'react';

export interface TableDropdownMenuProps extends Omit<ButtonProps, 'type'> {
    /**
     * The TipTap editor instance.
     */
    editor?: Editor;
    /**
     * Whether the dropdown should be hidden when no table is available
     * @default false
     */
    hideWhenUnavailable?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
}

interface TableAction {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    action?: (editor: Editor) => void;
    opensInsertPopover?: boolean;
    disabled?: boolean | ((editor: Editor) => boolean);
    separator?: boolean;
    isActive?: boolean | ((editor: Editor) => boolean);
}

function createDefaultTableHeaderCell() {
    return {
        type: 'tableHeader',
        attrs: {
            colspan: 1,
            rowspan: 1,
            colwidth: null,
            scope: '',
            primaryKey: false,
        },
        content: [{ type: 'paragraph', content: [{ type: 'text', text: ' ' }] }],
    };
}

function createDefaultTableCell() {
    return {
        type: 'tableCell',
        attrs: {
            colspan: 1,
            rowspan: 1,
            colwidth: null,
        },
        content: [{ type: 'paragraph', content: [{ type: 'text', text: ' ' }] }],
    };
}

export function buildDefaultTableContent(rows: number, cols: number) {
    const headerRow = {
        type: 'tableRow',
        content: Array.from({ length: cols }, () => createDefaultTableHeaderCell()),
    };
    const dataRows = Array.from({ length: Math.max(rows - 1, 0) }, () => ({
        type: 'tableRow',
        content: Array.from({ length: cols }, () => createDefaultTableCell()),
    }));

    return [
        {
            type: 'table',
            attrs: {
                isDatabaseTable: false,
                isLayoutGrid: false,
            },
            content: [headerRow, ...dataRows],
        },
    ];
}

export const defaultTableContent = buildDefaultTableContent(3, 3);

const tableActions: TableAction[][] = [
    [
        {
            label: 'Insert table',
            icon: Table,
            opensInsertPopover: true,
        },
        {
            label: 'Delete table',
            icon: Trash2,
            action: editor => editor.chain().focus().deleteTable().run(),
        },
    ],
    [
        {
            label: 'Add column before',
            icon: BetweenVerticalStart,
            action: editor => editor.chain().focus().addColumnBefore().run(),
        },
        {
            label: 'Add column after',
            icon: BetweenVerticalStart,
            action: editor => editor.chain().focus().addColumnAfter().run(),
        },
        {
            label: 'Delete column',
            icon: Grid2X2X,
            action: editor => editor.chain().focus().deleteColumn().run(),
            separator: true,
        },
    ],
    [
        {
            label: 'Add row before',
            icon: BetweenHorizonalStart,
            action: editor => editor.chain().focus().addRowBefore().run(),
        },
        {
            label: 'Add row after',
            icon: BetweenHorizonalStart,
            action: editor => editor.chain().focus().addRowAfter().run(),
        },
        {
            label: 'Delete row',
            icon: Grid2X2X,
            action: editor => editor.chain().focus().deleteRow().run(),
        },
    ],
    [
        {
            label: 'Merge cells',
            icon: TableCellsMerge,
            action: editor => editor.chain().focus().mergeCells().run(),
        },
        {
            label: 'Split cell',
            icon: TableCellsSplit,
            action: editor => editor.chain().focus().splitCell().run(),
            separator: true,
        },
        {
            label: 'Header column',
            icon: Columns3Cog,
            action: editor => editor.chain().focus().toggleHeaderColumn().run(),
            disabled: (editor: Editor) =>
                !isSelectionDescendantOf(editor, 'table') || getParentNodeOfType(editor, 'table')?.attrs.isLayoutGrid === true,
            isActive: (editor: Editor) => {
                const table = getTable(editor);
                return table ? columnIsHeader(table.map, table.node, 0) : false;
            },
        },
        {
            label: 'Header row',
            icon: Columns3Cog,
            action: editor => editor.chain().focus().toggleHeaderRow().run(),
            disabled: (editor: Editor) =>
                !isSelectionDescendantOf(editor, 'table') || getParentNodeOfType(editor, 'table')?.attrs.isLayoutGrid === true,
            isActive: (editor: Editor) => {
                const table = getTable(editor);
                return table ? rowIsHeader(table.map, table.node, 0) : false;
            },
        },
        {
            label: 'Header cell',
            icon: Columns3Cog,
            action: editor => editor.chain().focus().toggleHeaderCell().run(),
            disabled: (editor: Editor) =>
                !isSelectionDescendantOf(editor, 'table') || getParentNodeOfType(editor, 'table')?.attrs.isLayoutGrid === true,
            isActive: (editor: Editor) => {
                return editor.isActive('tableHeader');
            },
        },
    ],
    [
        {
            label: 'Database table',
            icon: Database,
            action: editor => editor.chain().focus().toggleDatabaseTable().run(),
            disabled: (editor: Editor) =>
                !isSelectionDescendantOf(editor, 'table') || getParentNodeOfType(editor, 'table')?.attrs.isLayoutGrid === true,
            isActive: (editor: Editor) => {
                return getParentNodeOfType(editor, 'table')?.attrs.isDatabaseTable === true;
            },
        },
        {
            label: 'Primary key',
            icon: KeyRound,
            action: editor => editor.chain().focus().togglePrimaryKey().run(),
            disabled: (editor: Editor) => {
                const table = getParentNodeOfType(editor, 'table');
                return (
                    !isSelectionDescendantOf(editor, 'tableHeader') ||
                    table?.attrs.isDatabaseTable === false ||
                    table?.attrs.isLayoutGrid === true
                );
            },
            isActive: (editor: Editor) => {
                return getParentNodeOfType(editor, 'tableHeader')?.attrs.primaryKey === true;
            },
        },
        {
            label: 'Foreign key',
            icon: KeyRound,
            action: editor => editor.chain().focus().toggleForeignKey().run(),
            disabled: (editor: Editor) => {
                const table = getParentNodeOfType(editor, 'table');
                return (
                    !isSelectionDescendantOf(editor, 'tableHeader') ||
                    table?.attrs.isDatabaseTable === false ||
                    table?.attrs.isLayoutGrid === true
                );
            },
            isActive: (editor: Editor) => {
                return getParentNodeOfType(editor, 'tableHeader')?.attrs.foreignKey === true;
            },
        },
    ],
];

export function TableDropdownMenu({ editor: providedEditor, hideWhenUnavailable = false, onOpenChange, ...props }: TableDropdownMenuProps) {
    const { editor } = useTiptapEditor(providedEditor);
    const [isOpen, setIsOpen] = React.useState(false);
    const [insertPopoverOpen, setInsertPopoverOpen] = React.useState(false);

    const tableInSchema = isNodeInSchema('table', editor);
    const isTableActive = editor?.isActive('table') ?? false;

    const handleOpenChange = React.useCallback(
        (open: boolean) => {
            setIsOpen(open);
            onOpenChange?.(open);
        },
        [onOpenChange],
    );

    const handleActionClick = React.useCallback(
        (action: TableAction) => {
            if (!editor) return;

            if (action.opensInsertPopover) {
                handleOpenChange(false);
                setInsertPopoverOpen(true);
                return;
            }

            action.action?.(editor);
            handleOpenChange(false);
        },
        [editor, handleOpenChange],
    );

    const show = React.useMemo(() => {
        if (!tableInSchema || !editor) {
            return false;
        }

        if (hideWhenUnavailable) {
            if (isNodeSelection(editor.state.selection)) {
                return false;
            }
        }

        return true;
    }, [editor, hideWhenUnavailable, tableInSchema]);

    const renderActions = (group: TableAction[]) => {
        return group.map(action => {
            let disabled = false;
            if (typeof action.disabled === 'function' && editor) {
                disabled = action.disabled(editor);
            } else if (typeof action.disabled === 'boolean') {
                disabled = action.disabled;
            }

            let isActive = false;
            if (typeof action.isActive === 'function' && editor) {
                isActive = action.isActive(editor);
            } else if (typeof action.isActive === 'boolean') {
                isActive = action.isActive;
            }

            return (
                <Fragment key={action.label}>
                    <DropdownMenuItem
                        asChild
                        disabled={disabled}
                        onSelect={() => handleActionClick(action)}
                    >
                        <Button
                            type="button"
                            disabled={disabled}
                            data-active-state={isActive ? 'on' : 'off'}
                            data-style="ghost"
                            className="w-full"
                        >
                            <div className="flex-start flex w-full">
                                <action.icon className="tiptap-button-icon mr-2 h-4 w-4" />
                                <span>{action.label}</span>
                            </div>
                        </Button>
                    </DropdownMenuItem>
                    {action.separator && <Separator orientation="horizontal" />}
                </Fragment>
            );
        });
    };

    if (!show || !editor || !editor.isEditable) {
        return null;
    }

    return (
        <Popover
            open={insertPopoverOpen}
            onOpenChange={setInsertPopoverOpen}
        >
            <DropdownMenu
                open={isOpen}
                onOpenChange={handleOpenChange}
            >
                <PopoverAnchor asChild>
                    <DropdownMenuTrigger asChild>
                        <Button
                            type="button"
                            data-style="ghost"
                            data-active-state={isTableActive ? 'on' : 'off'}
                            role="button"
                            tabIndex={-1}
                            aria-label="Table options"
                            tooltip="zyTable"
                            {...props}
                        >
                            <Table className="tiptap-button-icon" />
                            <ChevronDownIcon className="tiptap-button-dropdown-small" />
                        </Button>
                    </DropdownMenuTrigger>
                </PopoverAnchor>

                <DropdownMenuContent>
                    <Card>
                        <CardBody>
                            {tableActions.map((actionGroup, index) => {
                                return (
                                    <Fragment key={index}>
                                        <DropdownMenuGroup>{renderActions(actionGroup)}</DropdownMenuGroup>
                                    </Fragment>
                                );
                            })}
                        </CardBody>
                    </Card>
                </DropdownMenuContent>
            </DropdownMenu>

            <PopoverContent
                align="start"
                side="bottom"
            >
                <Card>
                    <CardBody>
                        <TableInsertGridPanel
                            editor={editor}
                            mode="table"
                            focusInputs={insertPopoverOpen}
                            onInserted={() => setInsertPopoverOpen(false)}
                        />
                    </CardBody>
                </Card>
            </PopoverContent>
        </Popover>
    );
}

export default TableDropdownMenu;
